import { useEffect, useState } from 'react';

import { AnimatePresence, motion } from 'framer-motion';

interface AnimatedCreditsProps {
  credits: number;
  className?: string;
}

export const AnimatedCredits = ({ credits, className = '' }: AnimatedCreditsProps) => {
  const [displayCredits, setDisplayCredits] = useState(credits);
  const [isAnimating, setIsAnimating] = useState(false);
  const [creditsDiff, setCreditsDiff] = useState(0);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    // On first load, just set credits without animation
    if (!isInitialized) {
      setDisplayCredits(credits);
      setIsInitialized(true);
      return;
    }

    // Only animate on actual changes after initialization
    if (credits !== displayCredits) {
      const diff = credits - displayCredits;
      setCreditsDiff(diff);
      setIsAnimating(true);

      // Animate the credits change
      const startCredits = displayCredits;
      const endCredits = credits;
      // const duration = 800; // 800ms animation
      const steps = 30;
      const increment = (endCredits - startCredits) / steps;
      let currentStep = 0;

      const animate = () => {
        currentStep++;
        const currentCredits = startCredits + increment * currentStep;

        if (currentStep < steps) {
          setDisplayCredits(Math.round(currentCredits));
          requestAnimationFrame(animate);
        } else {
          setDisplayCredits(endCredits);
          setTimeout(() => {
            setIsAnimating(false);
            setCreditsDiff(0);
          }, 500);
        }
      };

      requestAnimationFrame(animate);
    }
  }, [credits, displayCredits, isInitialized]);

  return (
    <div className={`relative flex items-center gap-2 ${className}`}>
      {/* Credits display with glow effect when changing */}
      <motion.span
        className={`text-sm font-medium transition-all duration-300 ${
          isAnimating
            ? creditsDiff > 0
              ? 'text-green-500 drop-shadow-glow-green'
              : 'text-red-500 drop-shadow-glow-red'
            : 'text-gray-800'
        }`}
        animate={{
          scale: isAnimating ? [1, 1.1, 1] : 1,
        }}
        transition={{ duration: 0.5 }}
      >
        {displayCredits.toLocaleString()}
      </motion.span>

      {/* Animated difference indicator */}
      <AnimatePresence>
        {isAnimating && creditsDiff !== 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.5 }}
            animate={{ opacity: 1, y: -20, scale: 1 }}
            exit={{ opacity: 0, y: -30, scale: 0.5 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
            className={`absolute -top-8 left-1/2 transform -translate-x-1/2 text-xs font-bold pointer-events-none ${
              creditsDiff > 0 ? 'text-green-400' : 'text-red-400'
            }`}
          >
            {creditsDiff > 0 ? '+' : ''}
            {creditsDiff}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Particle effects for credit changes */}
      <AnimatePresence>
        {isAnimating && (
          <>
            {/* Sparkle effect for positive changes */}
            {creditsDiff > 0 && (
              <>
                {[...Array(3)].map((_, i) => (
                  <motion.div
                    key={`sparkle-${i}`}
                    initial={{
                      opacity: 0,
                      scale: 0,
                      x: 0,
                      y: 0,
                    }}
                    animate={{
                      opacity: [0, 1, 0],
                      scale: [0, 1, 0],
                      x: (i - 1) * 15,
                      y: -10 - i * 5,
                    }}
                    transition={{
                      duration: 0.6,
                      delay: i * 0.1,
                      ease: 'easeOut',
                    }}
                    className="absolute w-1 h-1 bg-green-400 rounded-full pointer-events-none"
                    style={{
                      boxShadow: '0 0 6px #4ade80',
                    }}
                  />
                ))}
              </>
            )}

            {/* Pulse effect for negative changes */}
            {creditsDiff < 0 && (
              <motion.div
                initial={{ opacity: 0.7, scale: 1 }}
                animate={{ opacity: 0, scale: 2 }}
                transition={{ duration: 0.5 }}
                className="absolute inset-0 rounded-lg border border-red-400 pointer-events-none"
              />
            )}
          </>
        )}
      </AnimatePresence>
    </div>
  );
};
